import Cookies from "js-cookie";
import { Campaign, Client } from "./domains"
import { Constants, SdkConfig } from "./globals";
import { PopupContext } from "./libs";
import { CampaignPredicate } from "./predicate/index.js";
import { abTestService, campaignService, collectService, feedbackService, servicesService } from "./services"
import ExpiryLocalStorageUtils from "./utils/ExpiryLocalStorageUtils";

class Main {
  constructor() {
    this.client = new Client()
    this.popupContext = new PopupContext()
    this.campaignPredicate = new CampaignPredicate({ popupContext: this.popupContext })
    this.inspectCampaignPreview()
  }

  /**
   * SDK 초기화
   * @param serviceId
   * @param config {}
   * @param userAttr {}
   */
  async init(serviceId, config, userAttr) {
    const url = new URL(location.href)
    if (['previewCampaign', 'previewAbTest'].includes(url.searchParams.get('_cdpMode'))) {
      return
    }
    try {
      SdkConfig.initConfig(config)
      await this.verifyUser(serviceId, userAttr)
      await this.updateUserAttr(userAttr)
      const delegator = SdkConfig.config.delegateCampaignHandler
      if (delegator) {
        delegator(this.client)
      } else {
        this.handleCampaignDelegator(this.client)
      }
    } catch (error) {
      console.error("CDP SDK 초기화 중 오류가 발생했습니다.")
      if (SdkConfig.isDebugMode()) {
        console.error(error)
      }
    }
  }

  async handleCampaignDelegator(client) {
    const resolvedClient = Client.isClient(client) ? client : Client.fromJson(client)
    if (resolvedClient.contains('deviceId')) {
      resolvedClient.set('clientId', resolvedClient.get('deviceId'))
    }
    feedbackService.process(resolvedClient)
        .then(({ data: { data: { campaignList, abTestList } }, headers }) => {
          if (SdkConfig.isDebugMode()) {
            console.debug('###DEBUG >> CampaignList --> ', campaignList)
            console.debug('###DEBUG >> AbTestList --> ', abTestList)
          }
          this.campaignPredicate.test({
            campaignList,
            abTestList,
            client: resolvedClient,
            originReferrer: headers.get('origin-referrer')
          })
          this.sendPageViewEvent()
        })
        .catch(reason => SdkConfig.isDebugMode() && console.error(reason))
  }

  async verifyUser(serviceId, userAttr) {
    await servicesService.verifyServiceId(serviceId)
    this.client.set('serviceId', serviceId)
    if ('userId' in userAttr && userAttr.userId !== undefined && userAttr.userId !== null && userAttr.userId !== '' && userAttr.userId !== 'null' && userAttr.userId !== 'undefined') {
      this.client.set('userId', userAttr.userId)
    } else {
      this.client.set('userId', 'ANONYMOUS')
    }
    delete userAttr.userId
  }

  /**
   * 회원정보 수정 요청
   * @param attributes {}
   */
  async updateUserAttr(attributes) {
    // console.log('this.collectLatLngIfAllowed()', await this.collectLatLngIfAllowed())
    await collectService.updateUser(this.client, attributes).catch(reason => SdkConfig.isDebugMode() && console.error(reason))
  }

  // async collectLatLngIfAllowed() {
  //   if (!("geolocation" in navigator)) {
  //     return { ok: false, reason: "GEOLOCATION_NOT_SUPPORTED" };
  //   }
  //   let permState = "unknown";
  //   if (navigator.permissions?.query) {
  //     try {
  //       const status = await navigator.permissions.query({ name: "geolocation" });
  //       permState = status.state; // "granted" | "prompt" | "denied"
  //       if (permState !== "granted") {
  //         return { ok: false, reason: `PERMISSION_${permState.toUpperCase()}` };
  //       }
  //     } catch {
  //       // 권한 상태 확인 실패 시, 아래에서 직접 getCurrentPosition 시도 가능(원하시면 분기 추가)
  //       return { ok: false, reason: "PERMISSION_QUERY_FAILED" };
  //     }
  //   } else {
  //     // Permissions API 미지원이면 “허용 여부”를 사전에 확정할 수 없어서
  //     // 정책적으로 여기서 수집을 막거나(아래처럼), 곧바로 getCurrentPosition을 호출해도 됩니다.
  //     return { ok: false, reason: "PERMISSIONS_API_NOT_SUPPORTED" };
  //   }
  //
  //   // 여기까지 왔으면 granted인 상태
  //   const pos = await new Promise((resolve, reject) => {
  //     navigator.geolocation.getCurrentPosition(resolve, reject, {
  //       enableHighAccuracy: true,
  //       timeout: 8000,
  //       maximumAge: 60_000,
  //     });
  //   });
  //
  //   console.log('pos', pos)
  //
  //   const { latitude, longitude, accuracy } = pos.coords;
  //   return {
  //     ok: true,
  //     lat: latitude,
  //     lng: longitude,
  //     accuracy,
  //     timestamp: pos.timestamp,
  //   };
  // }

  sendPageViewEvent() {
    const url = new URL(location.href)
    if (url.searchParams.get('_cdpMode') === 'previewCampaign') {
      return
    }
    const inflowCampaignList = ExpiryLocalStorageUtils.getArrayValueAsJson(Constants.STORAGE_KEY_CAMPAIGN_INFLOW_LIST, 30)
    const exposureCampaignList = Cookies.get(Constants.COOKIE_NAME_CAMPAIGN_EXPOSURE_LIST)
    collectService.collect(this.client, {
      type: "PageView",
      inflowCampaignIds: inflowCampaignList.map(inflowCampaign => inflowCampaign.campaignId),
      exposureCampaignIds: exposureCampaignList?.split('|') || [],
    }).catch(reason => SdkConfig.isDebugMode() && console.error(reason))
  }

  async sendUserActionEvent({ eventId, customAttr }) {
    const inflowCampaignList = ExpiryLocalStorageUtils.getArrayValueAsJson(Constants.STORAGE_KEY_CAMPAIGN_INFLOW_LIST, 30)
    const exposureCampaignList = Cookies.get(Constants.COOKIE_NAME_CAMPAIGN_EXPOSURE_LIST)
    await collectService.collect(this.client, {
      type: "UserAction",
      inflowCampaignIds: inflowCampaignList.map(inflowCampaign => inflowCampaign.campaignId),
      exposureCampaignIds: exposureCampaignList?.split('|') || [],
      action: {
        name: eventId,
        customAttr: {
          ...customAttr
        }
      },
    })
  }

  async inspectCampaignPreview() {
    const url = new URL(location.href)
    const cdpMode = url.searchParams.get('_cdpMode')
    if (cdpMode === 'previewCampaign' || cdpMode === 'previewAbTest') {
      if (url.searchParams.has('_previewId')) {
        const variantKey = url.searchParams.get('_variantKey')
        const id = url.searchParams.get('_previewId')
        const campaignItem = await (() => {
          if (cdpMode === 'previewCampaign') {
            return campaignService.fetch(id)
          }
          if (cdpMode === 'previewAbTest') {
            return abTestService.fetch(id)
          }
        })()
        const campaign = new Campaign({
          ...campaignItem,
          variantKey,
          $reference: cdpMode === 'previewAbTest' ? 'AB_TEST' : 'CAMPAIGN',
          isPreview: true
        })
        const popup = campaign.getPreviewPopup()
        if (popup) {
          this.popupContext.addPopupThenShow(popup)
        }
      }
    }
  }
}

export default Main
